import { createReducer, Action, on} from '@ngrx/store';
import { createEntityAdapter, Dictionary, EntityAdapter, EntityState } from '@ngrx/entity';
import * as fromActions from './related-news.actions';
import * as fromChannelDetails from '@app/channel/store/channel-details';

import { SortDirection } from '@rds/angular-components';
import { Channel, ChannelDetails, News, NewsSuggestion } from '@app/types';
import { Pagination } from '@app/types/pagination';
import { DefaultNewsTableFilters, FilterDefinition, NewsTableFilters, NEWS_APPROVAL_STATUSES, NEWS_STATUSES, NEWS_TYPES, SelectFilterDefinition, NEWS_STATUSES_EXTENDED, INITIAL_ALL_NEWS_FILTERS, INITIAL_MY_NEWS_FILTERS, INITIAL_MY_SUGGESTED_FILTERS, INITIAL_MY_ARCHIVED_FILTERS, INITIAL_RELATED_NEWS_FILTERS } from '@app/utils/table/table-filters/filters-model';

export const feature = 'related-news';

export interface TableState {
  data: EntityState<Partial<News>>,
  pagination: Pagination,
  sort: {
    active: string;
    direction: SortDirection
  },
  defaultSort: {
    active: string;
    direction: SortDirection
  },
  filters: NewsTableFilters;
  defaultFilters: NewsTableFilters;
  isLoading: boolean;
  resetIndex: boolean;
}

export const adapter: EntityAdapter<Partial<News>> =
 createEntityAdapter<Partial<News>>({
  selectId: (e) => e.id
 });

 export const suggestionAdapter: EntityAdapter<Partial<NewsSuggestion>> =
 createEntityAdapter<Partial<NewsSuggestion>>({
  selectId: (e) => `c${e.channel.id}n${e.id}`
 });

export interface State {
  newsPreviewDetails: News;
  selected: EntityState<Partial<News>>;
  relatedNews: TableState;
}

export const initialState: State = {
  newsPreviewDetails: null,
  selected: adapter.getInitialState(),
  relatedNews: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 6,
    },
    sort: {
      active: 'createdDate',
      direction: 'desc'
    },
    defaultSort: {
      active: 'createdDate',
      direction: 'desc'
    },
    filters: INITIAL_RELATED_NEWS_FILTERS,
    defaultFilters: INITIAL_RELATED_NEWS_FILTERS,
    isLoading: false,
    resetIndex: false,
  },
};

const tableReducer = createReducer(
  initialState,
  on(fromActions.getRelatedNewsRequest, (state) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      isLoading: true
    }
  })),
  on(fromActions.getRelatedNewsSuccess, fromActions.getRelatedNewsFailure, (state) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      isLoading: false
    }
  })),
  on(fromActions.getRelatedNewsSuccess, (state, {data, pagination}) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      data: adapter.setAll(data, state.relatedNews.data),
      pagination,
      resetIndex: false,
    }
  })),
  on(fromActions.changeRelatedNewsFilters, (state, {filters}) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeRelatedNewsSort, (state, {active, direction}) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeRelatedNewsPagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    relatedNews: {
      ...state.relatedNews,
      pagination: {
        ...state.relatedNews.pagination,
        pageIndex,
        pageSize
      }
    }
  })),
  on(fromActions.newsSelectionChanged, (state, {news}) => ({
    ...state,
    selected: adapter.setAll(news, state.selected)
  })),
  on(fromActions.addRelatedNews, (state, {news}) => ({
    ...state,
    selected: adapter.upsertMany(news, state.selected)
  })),
  on(fromActions.removeRelatedNews, (state, {id}) => ({
    ...state,
    selected: adapter.removeOne(id, state.selected)
  })),
  on(fromActions.clearAll, (state) => ({
    ...initialState
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return tableReducer(state, action);
}

export const getChannelId = (details: ChannelDetails) => details?.id;

export const getRelatedNews = (state: State) => state.relatedNews;

export const getPagination = (table: TableState) => table.pagination;
export const getSort = (table: TableState) => table.sort;
export const getDefaultSort = (table: TableState) => table.defaultSort;
export const getFilters = (table: TableState) => table.filters;
export const getDefaultFilters = (table: TableState) => table.defaultFilters;
export const getIsLoading = (table: TableState) => table.isLoading;

export const getRequestData = ({pagination, sort, filters, resetIndex}: TableState) => ({
  pageIndex: pagination.pageIndex,
  pageSize: pagination.pageSize,
  sort,
  filters,
  resetIndex});

export const getNewsData = (table: TableState) => adapter.getSelectors().selectAll(table.data);
export const getNewsSuggestionData = (table: TableState) => suggestionAdapter.getSelectors().selectAll(table.data);

export const getSelectedNews = (state: State) => adapter.getSelectors().selectAll(state.selected);

// Pagination fields
export const getPageSize = (pagination: Pagination) => pagination.pageSize;
export const getPageIndex = (pagination: Pagination) => pagination.pageIndex;
export const getTotalCount = (pagination: Pagination) => pagination.totalCount;

// Filters fields
export const getSearch = (filters: NewsTableFilters) => filters.search.value;

export const getEntitiesByIds = (entities: Dictionary<Channel>, ids: string[]): Channel[] => ids.map(id => entities[id]);
export { NewsTableFilters };

